import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Typography, Container, Stack, Paper, CircularProgress
} from '@mui/material';
import { MapContainer, TileLayer, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTheme } from '@mui/material/styles'; // Import useTheme for palette access

import { useAuth } from '../auth/AuthContext';

// Existing MapUpdater component (no changes needed)
const MapUpdater = ({ position }) => {
    const map = useMap();

    useEffect(() => {
        if (position) {
            map.setView(position, map.getZoom());
        }
    }, [position, map]);

    return null;
};

// New component to render the drawn polygon
const PolygonDrawer = ({ coordinates }) => {
    const map = useMap();

    useEffect(() => {
        if (coordinates && coordinates.length > 0) {
            const latLngs = coordinates[0].map(coord => [coord.lat, coord.lng]);
            const bounds = L.latLngBounds(latLngs);
            map.fitBounds(bounds);
        }
    }, [coordinates, map]);

    if (!coordinates || coordinates.length === 0) {
        return null;
    }

    const polygonPositions = coordinates[0].map(coord => [coord.lat, coord.lng]);

    return (
        <Polygon positions={polygonPositions} pathOptions={{ color: 'blue', weight: 3, opacity: 0.7 }} />
    );
};

const PropertyDetailScreen = () => {
    const { name } = useParams();
    const { dbUser } = useAuth();
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const theme = useTheme(); // Access the theme

    useEffect(() => {
        const fetchProperty = async () => {
            setLoading(true);
            setError('');
            if (dbUser && dbUser.misbolos && dbUser.misbolos.properties) {
                const foundProperty = dbUser.misbolos.properties.find(prop => prop.name.toLowerCase() === name.toLowerCase());
                if (foundProperty) {
                    setProperty(foundProperty);
                } else {
                    setError('Propiedad no encontrada.');
                }
            } else {
                setError('Datos de usuario o propiedades no disponibles.');
            }
            setLoading(false);
        };

        if (dbUser) {
            fetchProperty();
        }
    }, [name, dbUser]);

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 8, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ ml: 2 }}>Cargando propiedad...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h6" color="error">{error}</Typography>
            </Container>
        );
    }

    if (!property) {
        return (
            <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h6">No se pudo cargar la propiedad.</Typography>
            </Container>
        );
    }

    const defaultMapCenter = [40.4168, -3.7038]; // Madrid, Spain

    return (
        <Box sx={{
            minHeight: '100vh',
            background: theme.palette.background.default, // Use theme background
            py: 4
        }}>
            <Container maxWidth="md">
                <Paper elevation={3} sx={{ p: 4, borderRadius: theme.shape.borderRadius, bgcolor: theme.palette.background.paper }}>
                    <Stack spacing={3}>
                        <Typography variant="h4" component="h1" textAlign="center" gutterBottom sx={{ color: theme.palette.primary.main }}>
                            {property.name}
                        </Typography>

                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                            <Box component="span" fontWeight="bold">Detalles:</Box> {property.details || 'No hay detalles disponibles.'}
                        </Typography>
                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                            <Box component="span" fontWeight="bold">Acceso:</Box> {property.isPublic ? 'Público' : 'Privado'}
                        </Typography>

                        <Box sx={{
                            height: 400,
                            borderRadius: theme.shape.borderRadius,
                            overflow: 'hidden',
                            border: `1px solid ${theme.palette.divider}`, // Subtle border
                            mt: 3
                        }}>
                            <MapContainer
                                center={defaultMapCenter}
                                zoom={13}
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; OpenStreetMap contributors'
                                />
                                <PolygonDrawer coordinates={property.coordinates} />
                            </MapContainer>
                        </Box>
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
};

export default PropertyDetailScreen;